import { ChangePulseMarker, ProposedValue } from '@/features/ai-changeset/components/ProposedValue';
import type { NavigationStore } from '@/features/ai-changeset/hooks/useChangeNavigation';
import { DraftTag } from '@/features/menu/components/DraftTag';
import { scheduleOf, type SectionView } from '@/features/menu/utils/menu-draft';
import { describeHours } from '@/features/menu/utils/menu-draft-text';
import { useI18nStore, useT } from '@/i18n';
import { loc } from '@/utils/localized';
import { BlockTitle, Link as F7Link } from 'framework7-react';
import type { MouseEvent as ReactMouseEvent } from 'react';

const MUTED = '#8e8e93';

function scheduleSummary(schedule: unknown): string | null {
  const windows = scheduleOf(schedule)?.windows ?? [];
  const first = windows[0];
  if (!first) return null;
  return `${first.startTime}–${first.endTime}${windows.length > 1 ? ' …' : ''}`;
}

export function MenuSectionTitle({
  view,
  store,
  onActions,
}: {
  view: SectionView;
  store: NavigationStore;
  onActions: ((e: ReactMouseEvent) => void) | null;
}) {
  const t = useT();
  const q = t.menuQuickEdit;
  const locale = useI18nStore((s) => s.locale);
  const c = view.category;
  const sched = scheduleSummary(c.schedule);
  const isActive = view.active?.after ?? c.isActive;
  const removed = view.status === 'removed';
  return (
    <BlockTitle
      data-quick-edit-target={c.id}
      data-testid={`menu-section-title-${c.id}`}
      className={removed ? 'menu-section-title--removed' : undefined}
      style={{ position: 'relative', borderRadius: 8 }}
    >
      {view.changed && (
        <ChangePulseMarker store={store} id={c.id} testId={`menu-section-title-pulse-${c.id}`} />
      )}
      <span style={{ display: 'flex', alignItems: 'center', width: '100%', gap: 8 }}>
        <span
          style={{
            flex: 1,
            minWidth: 0,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          <span className={removed ? 'quick-edit-old' : undefined}>
            {view.name ? (
              <ProposedValue start before={view.name.before} after={view.name.after} />
            ) : (
              loc(c.name)
            )}
          </span>
          {view.hours ? (
            <span style={{ fontWeight: 400 }}>
              {'  ·  '}
              <ProposedValue
                start
                before={describeHours(view.hours.before, locale, q)}
                after={describeHours(view.hours.after, locale, q)}
              />
            </span>
          ) : (
            sched && <span style={{ color: MUTED, fontWeight: 400 }}>{`  ·  ${sched}`}</span>
          )}
          {view.active ? (
            <span style={{ fontWeight: 400 }}>
              {'  ·  '}
              <span className="ai-changed-text">{isActive ? q.shown : q.hidden}</span>
            </span>
          ) : (
            !isActive && (
              <span style={{ color: MUTED, fontWeight: 400 }}>{`  ·  ${t.menu.hidden}`}</span>
            )
          )}
          {view.status && <DraftTag status={view.status} testId={`menu-section-tag-${c.id}`} />}
        </span>
        {onActions && <F7Link iconF7="ellipsis" style={{ flexShrink: 0 }} onClick={onActions} />}
      </span>
    </BlockTitle>
  );
}
